import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Smile, Meh, Frown, X, Clock } from 'lucide-react';
import Button from '../ui/Button';
import { useAuth } from '../auth';
import { analytics } from '../../services/analytics';

const LS_KEY = 'portfolistic_pmf_survey_completed';
const LS_DISMISSED_KEY = 'portfolistic_pmf_survey_dismissed';
const DAYS_BEFORE_SURVEY = 14;

function isEligible(user) {
  if (!user?.metadata?.creationTime) return false;
  const created = new Date(user.metadata.creationTime);
  const now = new Date();
  const diffDays = (now - created) / (1000 * 60 * 60 * 24);
  return diffDays >= DAYS_BEFORE_SURVEY;
}

function wasCompleted() {
  return localStorage.getItem(LS_KEY) === 'true';
}

function wasDismissedThisSession() {
  return sessionStorage.getItem(LS_DISMISSED_KEY) === 'true';
}

const OPTIONS = [
  {
    value: 'very_disappointed',
    label: 'Very Disappointed',
    description: 'I rely on Portfolistic regularly',
    Icon: Frown,
    selectedClass: 'border-success-400/60 bg-success-500/20 text-success-300',
  },
  {
    value: 'somewhat_disappointed',
    label: 'Somewhat Disappointed',
    description: 'It is useful but I could manage without it',
    Icon: Meh,
    selectedClass: 'border-warning-400/60 bg-warning-500/20 text-warning-300',
  },
  {
    value: 'not_disappointed',
    label: 'Not Disappointed',
    description: 'It has not become essential for me yet',
    Icon: Smile,
    selectedClass: 'border-error-400/60 bg-error-500/20 text-error-300',
  },
];

/** Sean Ellis PMF Survey — auto-shows 14 days after signup, dismissible per-session. */
const PMFSurvey = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [alternative, setAlternative] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user && isEligible(user) && !wasCompleted() && !wasDismissedThisSession()) {
      // Small delay so it doesn't flash on page load
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleSubmit = useCallback(() => {
    if (!selected) return;

    const payload = {
      response: selected,
      alternative: selected === 'not_disappointed' ? alternative.trim() || null : null,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(LS_KEY, 'true');
    localStorage.setItem('portfolistic_pmf_survey_response', JSON.stringify(payload));

    analytics.track('pmf_survey_response', payload);

    setSubmitted(true);
  }, [selected, alternative]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(LS_DISMISSED_KEY, 'true');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
        role="presentation"
      />

      <div
        className="relative w-full max-w-md glass border border-white/10 rounded-2xl shadow-glass-lg p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pmf-survey-title"
      >
        {submitted ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4 text-success-400">&#10003;</div>
            <h3 className="text-xl font-semibold text-white mb-2">Thank you!</h3>
            <p className="text-white/60 mb-6">Your feedback shapes where Portfolistic goes next.</p>
            <Button variant="ghost" onClick={() => setVisible(false)}>Close</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary-400" />
                <h2 id="pmf-survey-title" className="text-lg font-semibold text-white">
                  Quick Question
                </h2>
              </div>
              <button
                onClick={handleDismiss}
                className="text-white/40 hover:text-white/80 transition-colors"
                aria-label="Close survey"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-white/90 text-sm font-medium mb-4">
              How disappointed would you be if you could no longer use Portfolistic?
            </p>

            <div className="space-y-2 mb-4">
              {OPTIONS.map((option) => {
                const isSelected = selected === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setSelected(option.value)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all
                      border
                      ${isSelected
                        ? option.selectedClass
                        : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-primary-400/50'
                      }
                    `}
                  >
                    <option.Icon className="w-5 h-5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium">{option.label}</div>
                      <div className="text-xs text-white/40">{option.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {selected === 'not_disappointed' && (
              <div className="mb-4">
                <label
                  htmlFor="pmf-alternative"
                  className="text-white/70 text-xs font-medium mb-1.5 block"
                >
                  What would you use instead?
                </label>
                <textarea
                  id="pmf-alternative"
                  value={alternative}
                  onChange={(e) => setAlternative(e.target.value)}
                  placeholder="e.g. manual resume, another tool..."
                  rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-primary-500/50 resize-none"
                />
              </div>
            )}

            <div className="flex justify-between items-center">
              <button
                onClick={handleDismiss}
                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 transition-colors"
              >
                <Clock className="w-3.5 h-3.5" />
                Remind me later
              </button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!selected}
                size="sm"
              >
                Submit
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PMFSurvey;
